/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { notification, Table, type TableProps } from 'antd';
import { IOrderItem } from '@/interfaces/order';
import TableComponent from '@/components/table';
import { formatNumber } from '@/utils/dataUtils';
import { sumBy } from 'lodash';
import { twMerge } from 'tailwind-merge';
import { DeleteFilled } from '@ant-design/icons';
import { IPackingList } from '@/interfaces/packingList';
import { isManager, isWareHouse } from '@/utils/permissionUtils';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import { IDataModal } from '@/interfaces/dataModal';
import { deletepackingList } from '@/adapters/packingList';

interface IProps {
  data: Partial<IPackingList>[];
  isPacking?: boolean;
  onDelete: () => void;
  orderNetWeight: number;
}

const _modal: IDataModal<Partial<IPackingList>> = {
  open: false,
};

const PackingListViewTable = ({ data, orderNetWeight, onDelete }: IProps) => {
  const [deleteModal, setDeleteModal] =
    useState<IDataModal<Partial<IPackingList>>>(_modal);
  const perManager = isManager();
  const perWarehouse = isWareHouse();

  const onDeletePackingList = async () => {
    try {
      await deletepackingList(Number(deleteModal?.id));
      notification.success({
        message: 'ลบ Packing List สำเร็จ',
      });
      onDelete();
      setDeleteModal({ ..._modal });
    } catch (error) {
      console.log(error);
    }
  };

  const columns: TableProps<Partial<IPackingList>>['columns'] = [
    {
      title: 'รหัสสินค้า',
      dataIndex: 'code',
      width: 130,
      className: 'font-bold',
      align: 'center',
      render: (_, rec) => (
        <div>
          {rec.packingListItems?.map(p => {
            return <p key={p.id}>{p.productCode}</p>;
          })}
        </div>
      ),
    },
    {
      title: 'DESCRITIPION',
      dataIndex: 'code',
      className: 'font-bold',
      render: (_, rec) => (
        <div>
          {rec.packingListItems?.map(p => {
            return <p key={p.id}>{p.productName}</p>;
          })}
        </div>
      ),
    },
    {
      title: 'LOT NUMBER',
      dataIndex: 'code',
      width: 130,
      className: 'font-bold',
      align: 'center',
      render: (_, rec) => (
        <div>
          {rec.packingListItems?.map(p => {
            return <p key={p.id}>{p.lotNumber}</p>;
          })}
        </div>
      ),
    },
    {
      title: 'QUANTITY (KG)',
      dataIndex: 'code',
      width: 130,
      className: 'font-bold',
      align: 'right',
      render: (_, rec) => (
        <div>
          {rec.packingListItems?.map(p => {
            return <p key={p.id}>{formatNumber(p.quantity || 0, 0)}</p>;
          })}
        </div>
      ),
    },
    {
      title: 'PACKAGES',
      dataIndex: 'code',
      width: 130,
      className: 'font-bold',
      align: 'right',
      render: (_, rec) => (
        <div>
          {rec.packingListItems?.map(p => {
            return <p key={p.id}>{formatNumber(p.package || 0, 0)}</p>;
          })}
        </div>
      ),
    },
    {
      title: ' PLTS x PACK x KG/PACK',
      dataIndex: 'dimension',
      width: 180,
      className: 'font-bold',
      align: 'center',
      render: _ => <div>{_}</div>,
    },
    {
      title: ' NET WEIGHT (KG)',
      dataIndex: 'netWeight',
      width: 130,
      className: 'font-bold',
      align: 'right',
      render: _ => formatNumber(_, 2),
    },
    {
      title: ' GROSS WEIGHT (KG)',
      dataIndex: 'grossWeight',
      width: 150,
      className: 'font-bold',
      align: 'right',
      render: _ => formatNumber(_, 2),
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      fixed: 'right',
      align: 'center',
      width: 40,
      className: 'font-bold',
      render: (_, rec) => (
        <DeleteFilled
          className="text-red-700"
          onClick={() =>
            setDeleteModal({
              id: rec.id,
              open: true,
              item: rec,
            })
          }
        />
      ),
      hidden: !(perManager || perWarehouse),
    },
  ];
  const netWeight = sumBy(data || [], 'netWeight');

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<Partial<IOrderItem>>
        columns={columns}
        criteria={{}}
        data={{
          data: data || [],
          totalRecords: 0,
        }}
        loading={false}
        onTableChange={() => {
          console.log();
        }}
        scrollLength={1300}
        showPagination={false}
        summary={() => {
          return (
            <Table.Summary fixed>
              <Table.Summary.Row className="bg-red-100">
                <Table.Summary.Cell
                  index={0}
                  colSpan={4}
                  className="font-bold items-end"
                  align="right"
                >
                  Net Weight (Order)
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={0}
                  colSpan={1}
                  className="font-bold items-end"
                  align="right"
                >
                  {formatNumber(orderNetWeight || 0, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={0}
                  colSpan={1}
                  className="font-bold items-end"
                  align="right"
                >
                  Net Weight (PackingList)
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={0}
                  colSpan={1}
                  className={twMerge(
                    'font-bold items-end text-red-500',
                    orderNetWeight === netWeight && 'text-green-700',
                  )}
                  align="right"
                >
                  {formatNumber(netWeight || 0, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={0}
                  colSpan={2}
                  className="font-bold items-end "
                  align="right"
                />
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
      />
      <DeleteConfirmModal
        content={`คุณต้องการลบใช่หรือไม่`}
        handleCancel={() => {
          setDeleteModal({ ..._modal });
        }}
        handleOk={async () => {
          await onDeletePackingList();
        }}
        open={deleteModal?.open}
        title={`Packing List`}
      />
    </div>
  );
};

export default PackingListViewTable;
